import { router, useForm, usePage, Link } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import type { FormEvent } from 'react'
import { useState, useRef } from 'react'
import AccountLayout from '@/layouts/AccountLayout'
import { useAccountPath } from '@/lib/useAccountPath'

interface SettingsProps {
  settings: {
    email_address: string
    consultancy_name: string | null
    primary_colour: string
    secondary_colour: string
    accent_colour: string
    logo_url: string | null
    privacy_policy_url: string | null
  }
  is_owner: boolean
  data_export: {
    ready: boolean
    generated_at: string | null
  }
}

export default function Settings() {
  const { settings, is_owner, data_export } = usePage<{ props: SharedProps & SettingsProps }>().props as unknown as SharedProps & SettingsProps
  const ap = useAccountPath()
  const [logoPreview, setLogoPreview] = useState<string | null>(settings.logo_url)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const brandingForm = useForm({
    consultancy_name: settings.consultancy_name || '',
    primary_colour: settings.primary_colour || '#4F46E5',
    secondary_colour: settings.secondary_colour || '#F9FAFB',
    accent_colour: settings.accent_colour || '#F59E0B',
    privacy_policy_url: settings.privacy_policy_url || '',
    logo: null as File | null,
    remove_logo: '0',
  })

  const passwordForm = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
  })

  const emailForm = useForm({
    email_address: settings.email_address,
    current_password: '',
  })

  const deleteAccountForm = useForm({
    current_password: '',
  })
  const [exporting, setExporting] = useState(false)

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      brandingForm.setData('logo', file)
      brandingForm.setData('remove_logo', '0')
      const reader = new FileReader()
      reader.onload = (ev) => setLogoPreview(ev.target?.result as string)
      reader.readAsDataURL(file)
    }
  }

  function handleRemoveLogo() {
    brandingForm.setData('remove_logo', '1')
    brandingForm.setData('logo', null)
    setLogoPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleBrandingSubmit(e: FormEvent) {
    e.preventDefault()
    brandingForm.patch(ap('/settings/branding'), { forceFormData: true })
  }

  function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault()
    passwordForm.patch(ap('/settings/password'), {
      onSuccess: () => {
        passwordForm.reset()
      },
    })
  }

  function handleEmailSubmit(e: FormEvent) {
    e.preventDefault()
    if (!confirm('Changing your email will require re-confirmation and you will be logged out. Continue?')) return
    emailForm.patch(ap('/settings/email'))
  }

  function handleExport() {
    setExporting(true)
    router.post(ap('/settings/export'), {}, { onFinish: () => setExporting(false) })
  }

  function handleDeleteAccountSubmit(e: FormEvent) {
    e.preventDefault()
    deleteAccountForm.delete(ap('/settings/account'))
  }

  return (
    <AccountLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Account Settings</h1>
        </div>

        {/* Branding Section */}
        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Branding</h2>
            <p className="text-sm text-base-content/60">
              Your default branding for all surveys. Individual surveys can override these settings.
            </p>

            <form onSubmit={handleBrandingSubmit} className="space-y-4 mt-4">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Consultancy name</legend>
                <input
                  type="text"
                  className="input w-full"
                  value={brandingForm.data.consultancy_name}
                  onChange={(e) => brandingForm.setData('consultancy_name', e.target.value)}
                  placeholder="e.g. Apex Leadership Consulting"
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Logo</legend>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="size-16 rounded-lg object-contain border border-base-300 bg-white p-1"
                    />
                  ) : (
                    <div className="size-16 rounded-lg border-2 border-dashed border-base-300 flex items-center justify-center">
                      <span className="iconify lucide--image size-6 text-base-content/30" />
                    </div>
                  )}
                  <div className="flex-1 space-y-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="file-input file-input-sm w-full"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleLogoChange}
                    />
                    {logoPreview && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs text-error"
                        onClick={handleRemoveLogo}
                      >
                        <span className="iconify lucide--trash-2 size-3" />
                        Remove logo
                      </button>
                    )}
                  </div>
                </div>
              </fieldset>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <fieldset className="fieldset">
                  <legend className="fieldset-legend">Primary colour</legend>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="size-10 rounded border border-base-300 cursor-pointer"
                      value={brandingForm.data.primary_colour}
                      onChange={(e) => brandingForm.setData('primary_colour', e.target.value)}
                    />
                    <input
                      type="text"
                      className="input input-sm flex-1 font-mono"
                      value={brandingForm.data.primary_colour}
                      onChange={(e) => brandingForm.setData('primary_colour', e.target.value)}
                      pattern="^#[0-9a-fA-F]{6}$"
                      maxLength={7}
                    />
                  </div>
                </fieldset>

                <fieldset className="fieldset">
                  <legend className="fieldset-legend">Secondary colour</legend>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="size-10 rounded border border-base-300 cursor-pointer"
                      value={brandingForm.data.secondary_colour}
                      onChange={(e) => brandingForm.setData('secondary_colour', e.target.value)}
                    />
                    <input
                      type="text"
                      className="input input-sm flex-1 font-mono"
                      value={brandingForm.data.secondary_colour}
                      onChange={(e) => brandingForm.setData('secondary_colour', e.target.value)}
                      pattern="^#[0-9a-fA-F]{6}$"
                      maxLength={7}
                    />
                  </div>
                </fieldset>

                <fieldset className="fieldset">
                  <legend className="fieldset-legend">Accent colour</legend>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="size-10 rounded border border-base-300 cursor-pointer"
                      value={brandingForm.data.accent_colour}
                      onChange={(e) => brandingForm.setData('accent_colour', e.target.value)}
                    />
                    <input
                      type="text"
                      className="input input-sm flex-1 font-mono"
                      value={brandingForm.data.accent_colour}
                      onChange={(e) => brandingForm.setData('accent_colour', e.target.value)}
                      pattern="^#[0-9a-fA-F]{6}$"
                      maxLength={7}
                    />
                  </div>
                </fieldset>
              </div>
              <div className="space-y-2 rounded-lg bg-base-200/50 p-3 text-xs text-base-content/70">
                <p className="flex items-start gap-2">
                  <span
                    className="mt-0.5 size-3 shrink-0 rounded-full border border-base-300"
                    style={{ backgroundColor: brandingForm.data.primary_colour }}
                  />
                  <span>
                    <span className="font-semibold text-base-content">Primary</span> — your main brand colour.
                    On survey pages it's the Submit button and selected answers; in reports it's the chart bars and section headings.
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <span
                    className="mt-0.5 size-3 shrink-0 rounded-full border border-base-300"
                    style={{ backgroundColor: brandingForm.data.secondary_colour }}
                  />
                  <span>
                    <span className="font-semibold text-base-content">Secondary</span> — the page background on your
                    survey pages. A soft, light tint reads best behind the questions.
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <span
                    className="mt-0.5 size-3 shrink-0 rounded-full border border-base-300"
                    style={{ backgroundColor: brandingForm.data.accent_colour }}
                  />
                  <span>
                    <span className="font-semibold text-base-content">Accent</span> — highlights the average marker on
                    report charts (PDF and dashboard), so the mean stands out from the bars.
                  </span>
                </p>
              </div>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Privacy policy URL</legend>
                <input
                  type="url"
                  className="input w-full"
                  value={brandingForm.data.privacy_policy_url}
                  onChange={(e) => brandingForm.setData('privacy_policy_url', e.target.value)}
                  placeholder="https://your-consultancy.com/privacy"
                />
                <p className="text-xs text-base-content/60 mt-1">
                  Linked in the footer of your survey pages. Leave blank to use the Impact Check privacy policy.
                </p>
                {brandingForm.errors.privacy_policy_url && (
                  <p className="text-xs text-error mt-1">{brandingForm.errors.privacy_policy_url}</p>
                )}
              </fieldset>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={brandingForm.processing}
                >
                  {brandingForm.processing && <span className="loading loading-spinner loading-sm" />}
                  Save branding
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Two-factor authentication */}
        <div className="card bg-base-100">
          <div className="card-body">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="card-title text-lg">Two-factor authentication</h2>
                <p className="text-sm text-base-content/60 mt-1">
                  Add a second step at login with an authenticator app.
                </p>
              </div>
              <Link href={ap('/settings/mfa')} className="btn btn-outline btn-sm">
                Manage
              </Link>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Change Password</h2>

            <form onSubmit={handlePasswordSubmit} className="space-y-4 mt-4">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Current password</legend>
                <input
                  type="password"
                  className="input w-full"
                  value={passwordForm.data.current_password}
                  onChange={(e) => passwordForm.setData('current_password', e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">New password</legend>
                <input
                  type="password"
                  className="input w-full"
                  value={passwordForm.data.password}
                  onChange={(e) => passwordForm.setData('password', e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Confirm new password</legend>
                <input
                  type="password"
                  className="input w-full"
                  value={passwordForm.data.password_confirmation}
                  onChange={(e) => passwordForm.setData('password_confirmation', e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </fieldset>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={passwordForm.processing}
                >
                  {passwordForm.processing && <span className="loading loading-spinner loading-sm" />}
                  Update password
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Change Email */}
        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Change Email</h2>
            <p className="text-sm text-base-content/60">
              Changing your email will require re-confirmation. You will be logged out until the new email is confirmed.
            </p>

            <form onSubmit={handleEmailSubmit} className="space-y-4 mt-4">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">New email address</legend>
                <input
                  type="email"
                  className="input w-full"
                  value={emailForm.data.email_address}
                  onChange={(e) => emailForm.setData('email_address', e.target.value)}
                  required
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Current password</legend>
                <input
                  type="password"
                  className="input w-full"
                  value={emailForm.data.current_password}
                  onChange={(e) => emailForm.setData('current_password', e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </fieldset>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={emailForm.processing}
                >
                  {emailForm.processing && <span className="loading loading-spinner loading-sm" />}
                  Update email
                </button>
              </div>
            </form>
          </div>
        </div>

        {is_owner && (
          <>
            {/* Data Export */}
            <div className="card bg-base-100">
              <div className="card-body">
                <h2 className="card-title text-lg">Export your data</h2>
                <p className="text-sm text-base-content/60">
                  Download everything in your organization as a zip of CSV files: surveys, responses,
                  respondents, and facilitators. We'll email you when it's ready.
                </p>

                {data_export.ready && (
                  <div role="alert" className="alert alert-soft alert-info text-sm mt-2">
                    <span className="iconify lucide--file-archive size-4" />
                    <span>
                      An export from{' '}
                      {data_export.generated_at &&
                        new Date(data_export.generated_at).toLocaleString()}{' '}
                      is ready.
                    </span>
                    <a href={ap('/settings/export/download')} className="btn btn-sm">
                      Download
                    </a>
                  </div>
                )}

                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleExport}
                    disabled={exporting}
                  >
                    {exporting && <span className="loading loading-spinner loading-sm" />}
                    {data_export.ready ? 'Generate a new export' : 'Generate export'}
                  </button>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="card bg-base-100 border border-error/30">
              <div className="card-body">
                <h2 className="card-title text-lg text-error">Delete organization</h2>
                <p className="text-sm text-base-content/60">
                  Permanently deletes your organization and everything in it: surveys, responses,
                  respondents, facilitators, API keys, and team memberships. Any active subscription
                  is cancelled. This cannot be undone.
                </p>
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm text-error"
                    onClick={() =>
                      (document.getElementById('delete_account_modal') as HTMLDialogElement)?.showModal()
                    }
                  >
                    <span className="iconify lucide--trash-2 size-4" />
                    Delete organization
                  </button>
                </div>
              </div>
            </div>

            <dialog id="delete_account_modal" className="modal">
              <div className="modal-box">
                <h3 className="text-lg font-bold text-error">Delete this organization?</h3>
                <p className="py-2 text-sm text-base-content/70">
                  All surveys, responses, and respondent data will be permanently deleted. Consider
                  generating a data export first. Enter your password to confirm.
                </p>
                <form onSubmit={handleDeleteAccountSubmit} className="space-y-4">
                  <fieldset className="fieldset">
                    <legend className="fieldset-legend">Your password</legend>
                    <input
                      type="password"
                      className="input w-full"
                      value={deleteAccountForm.data.current_password}
                      onChange={(e) => deleteAccountForm.setData('current_password', e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </fieldset>
                  <div className="modal-action">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() =>
                        (document.getElementById('delete_account_modal') as HTMLDialogElement)?.close()
                      }
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-error btn-sm"
                      disabled={deleteAccountForm.processing || !deleteAccountForm.data.current_password}
                    >
                      {deleteAccountForm.processing && (
                        <span className="loading loading-spinner loading-sm" />
                      )}
                      Delete everything
                    </button>
                  </div>
                </form>
              </div>
              <form method="dialog" className="modal-backdrop">
                <button>close</button>
              </form>
            </dialog>
          </>
        )}
      </div>
    </AccountLayout>
  )
}
